import { Box, TextField } from '@mui/material';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import NumberFormat from 'react-number-format';

import { Page } from '../../components';
import { AdminTable } from '../../components/table/AdminTable';
import { IActivityTypesAPI } from '../../fetch';
import { useToastContext } from '../../hooks';
import { IActivityType } from '../../models';

export const ActivityTypes: FC = () => {
  const { showToast } = useToastContext();

  const [activityTypes, setActivityTypes] = useState<IActivityType[]>([]);
  const [isLoadingActivityTypes, setIsLoadingActivityTypes] = useState(false);
  const [isSavingActivityTypes, setIsSavingActivityTypes] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const fetchActivityTypesOnMount = useCallback(async () => {
    setIsLoadingActivityTypes(true);
    try {
      setActivityTypes(await IActivityTypesAPI.getAll());
    } catch (err) {
      const message = err instanceof Error && err.message;
      showToast('error', message || 'Failed to fetch activity types.');
    } finally {
      setIsLoadingActivityTypes(false);
    }
    // Ignore showToast
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    fetchActivityTypesOnMount();
  }, [fetchActivityTypesOnMount]);

  const columns = useMemo(() => {
    return [
      {
        accesor: 'name',
        Header: 'Activity Type',
        Cell: ({
          cell: {
            row: { original },
          },
        }: {
          cell: { row: { original: IActivityType; index: number } };
        }) => {
          return (
            <Box>
              <TextField
                fullWidth
                // Activity type is read-only.
                disabled
                placeholder="Activity Type"
                value={original.name}
              />
            </Box>
          );
        },
      },
      {
        accesor: 'netsuiteSpecialtyTypeId',
        Header: 'NetSuite Specialty Type ID',
        Cell: ({
          cell: {
            row: { original, index },
          },
        }: {
          cell: { row: { original: IActivityType; index: number } };
        }) => {
          const [netSuiteSpecialtyTypeId, setNetSuiteSpecialtyTypeId] = useState<null | number>(
            original.netSuiteSpecialtyTypeId
          );

          return (
            <Box>
              <NumberFormat
                fullWidth
                isNumericString
                decimalScale={0}
                size="small"
                allowNegative={false}
                customInput={TextField}
                value={netSuiteSpecialtyTypeId}
                onValueChange={values => {
                  const value = parseInt(values.value, 10);
                  if (Number.isNaN(value)) setNetSuiteSpecialtyTypeId(null);
                  setNetSuiteSpecialtyTypeId(value);
                }}
                onBlur={() => {
                  // Write changes
                  setActivityTypes((state: IActivityType[]) => {
                    if (state[index].netSuiteSpecialtyTypeId !== netSuiteSpecialtyTypeId) {
                      setHasChanges(true);
                    }

                    state[index].netSuiteSpecialtyTypeId = netSuiteSpecialtyTypeId;
                    state[index].isEdited = true;
                    return state;
                  });
                }}
                onFocus={(event: React.FocusEvent<HTMLInputElement>) => {
                  event.target?.select?.();
                }}
              />
            </Box>
          );
        },
      },
    ];
  }, []);

  return (
    <Page title="Activity Types">
      <AdminTable
        data={activityTypes}
        columns={columns}
        isLoading={isLoadingActivityTypes}
        hasChanges={hasChanges}
        isSaving={isSavingActivityTypes}
        onSave={async function upsertActivityTypes() {
          setIsSavingActivityTypes(true);
          try {
            const updatedList: IActivityType[] = await IActivityTypesAPI.upsert(activityTypes);
            setActivityTypes(updatedList);
            setHasChanges(false);
            showToast('success', 'Activity Type was updated successfully!');
          } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to update activity types.';
            showToast('error', message);
          } finally {
            setIsSavingActivityTypes(false);
          }
        }}
      />
    </Page>
  );
};
